/*
 * TrackerBase Admin Subscription Management Controller
 *
 */

import angular from "angular";
import kendo from "@progress/kendo-ui";
import "kendo.timezones";

import ngSanitize from "angular-sanitize";
import ngAnimate from "angular-animate";
import ngAria from "angular-aria";
import uiRouter from "@uirouter/angularjs";
import uiBootstrap from "angular-ui-bootstrap";

import Assert from "tbAssert";
import DependencyListBuilder from "../../utility/DependencyListBuilder";

import TbAppConstants from "../../config/tbAppConstants";

import Recipient from "../../models/Recipient";
import Subscription from "../../models/Subscription";
import MessageQueue from "../../models/MessageQueue";
import RecipientFilterSpecification from "./RecipientFilterSpecification";
import RecipientSortSpecification from "./RecipientSortSpecification";

import ActiveRecipientSubscriptionViewModel from "./ActiveRecipientSubscriptionViewModel";
import SubscriptionEditorError from "tbClient/views/trackerbase-admin/SubscriptionEditorError";

import SubscriptionWorkflowError from "./SubscriptionWorkflowError";
import SubscriptionWorkflowCancelledError from "./SubscriptionWorkflowCancelledError";

import tbAdministrationStateConstants from "./Constants";

import ConfirmDeleteDialogController from "../confirm-delete-dialog/ConfirmDeleteDialogController";
import ConfirmDeleteDialogResult from "../confirm-delete-dialog/ConfirmDeleteDialogResult";

import TargetSubscriptionsEditorDialogController from "./TargetSubscriptionsEditorDialog";
import OfficeTypeSubscriptionsEditorDialogController from "./OfficeTypeSubscriptionsEditorDialog";
import StateOfficeElectionSubscriptionsEditorDialogController from "./StateOfficeElectionSubscriptionsEditorDialog";
import StateEventSubscriptionsEditorDialogController from "./StateEventSubscriptionsEditorDialog";

import kendoDirectivesModuleName from "../../shims/kendoDirectives";

import SortFilterColumnHeaderComponentModule from "../../directives/SortFilterColumnHeader";
import RecipientDataServiceModule from "../../services/RecipientDataService";
import SubscriptionDataServiceModule from "../../services/SubscriptionDataService";
import UserInfoServiceModule from "../../services/UserInfoService";
import UserMessagesServiceModule from "../../services/UserMessagesService";


const mScopeSvc = new WeakMap();
const mDocumentSvc = new WeakMap();
const mTimeoutSvc = new WeakMap();
const mQSvc = new WeakMap();
const mAnchorScrollSvc = new WeakMap();
const mLogSvc = new WeakMap();
const mUIRouterStateSvc = new WeakMap();
const mUIRouterStateParams = new WeakMap();
const mUIBModalSvc = new WeakMap();
const mRecipientDataSvc = new WeakMap();
const mSubscriptionDataSvc = new WeakMap();
const mUserInfoSvc = new WeakMap();
const mUserMessagesSvc = new WeakMap();

const mActiveRecipient = new WeakMap();
const mActiveRecipientIdx = new WeakMap();
const mSelectedRecipientIds = new WeakMap();
const mRecipientData = new WeakMap();
const mRecipientDataAppendix = new WeakMap();

const errorMessageQueueType = "danger";
const statusMessageQueueType = "success";
const mErrorMessageQueue = new WeakMap();
const mStatusMessageQueue = new WeakMap();
const mOperationInProgress = new WeakMap();
const mCurrentProgress = new WeakMap();
const mCurrentOperationMax = new WeakMap();
const mCurrentOperationStatus = new WeakMap();

const mActiveRecipientSubscriptions = new WeakMap();
const mSubscriptionTypesInfo = new WeakMap();

const mInCreateRecipientMode = new WeakMap();
const mRecipientActionMenuIsOpen = new WeakMap();
const mShowRecipientBatchSelectionUi = new WeakMap();

const mShowRecipientTableFilters = new WeakMap();

const mCurrentFilterSpecs = new WeakMap();
const mCurrentSortSpecs = new WeakMap();

const mRecipientPagerData = new WeakMap();

const mSubsEditorParams = new WeakMap();
const mSubsEditorMessages = new WeakMap();

const subEditorParamsMap = new Map([
    ["Target","Target"],
    ["StateEvent","StateEvent"],
    ["OfficeType","OfficeType"],
    ["StateId","StateOfficeElection"],
    ["Office","StateOfficeElection"],
    ["Election","StateOfficeElection"]
]);

class TbSubscriptionManagementController {
    constructor($scope,
                $document,
                $timeout,
                $q,
                $anchorScroll,
                $log,
                $state,
                $transition$,
                $uibModal,
                RecipientData,
                SubscriptionData,
                UserInfoService,
                UserMessages
    ) {
        Assert.StrictAssert($scope != null, "Assertion Failure: Missing Scope Service Dependency");
        Assert.StrictAssert($document != null, "Assertion Failure: Missing Document Service Dependency");
        Assert.StrictAssert($timeout != null, "Assertion Failure: Missing Timeout Service Dependency");
        Assert.StrictAssert($q != null, "Assertion Failure: Missing Q Service Dependency");
        Assert.StrictAssert($anchorScroll != null, "Assertion Failure: Missing Anchor Scroll Service Dependency");
        Assert.StrictAssert($log != null, "Assertion Failure: Missing Log Service Dependency");

        Assert.StrictAssert($state != null, "Assertion Failure: Missing UI Router State Service Dependency");
        Assert.StrictAssert($transition$ != null, "Assertion Failure: Missing UI Router Transition Object Dependency");
        Assert.StrictAssert($uibModal != null, "Assertion Failure: Missing UIB Modal Service Dependency");

        Assert.StrictAssert(RecipientData != null, "Assertion Failure: Missing Recipient Data Service Dependency");
        Assert.StrictAssert(SubscriptionData != null, "Assertion Failure: Missing Subscription Data Service Dependency");
        Assert.StrictAssert(UserInfoService != null, "Assertion Failure: Missing User Info Service Dependency");
        Assert.StrictAssert(UserMessages != null, "Assertion Failure: Missing User Messages Service Dependency");

        mScopeSvc.set(this, $scope);
        mDocumentSvc.set(this, $document);
        mTimeoutSvc.set(this, $timeout);
        mQSvc.set(this, $q);
        mAnchorScrollSvc.set(this, $anchorScroll);
        mLogSvc.set(this, $log);

        mUIRouterStateSvc.set(this, $state);
        mUIRouterStateParams.set(this, $transition$.params());
        mUIBModalSvc.set(this, $uibModal);

        mUserInfoSvc.set(this, UserInfoService);
        mUserMessagesSvc.set(this, UserMessages);
        mRecipientDataSvc.set(this, RecipientData);
        mSubscriptionDataSvc.set(this, SubscriptionData);

        mErrorMessageQueue.set(this, new MessageQueue(errorMessageQueueType));
        mStatusMessageQueue.set(this, new MessageQueue(statusMessageQueueType));
        mOperationInProgress.set(this, false);
        mCurrentProgress.set(this, 0);
        mCurrentOperationMax.set(this, 100);
        mCurrentOperationStatus.set(this, statusMessageQueueType);

        mRecipientData.set(this, []);
        mRecipientDataAppendix.set(this, []);

        mActiveRecipient.set(this, null);
        mActiveRecipientIdx.set(this, null);

        mSelectedRecipientIds.set(this, new Set());

        mRecipientActionMenuIsOpen.set(this, false);
        mInCreateRecipientMode.set(this, false);

        mShowRecipientTableFilters.set(this, false);
        mShowRecipientBatchSelectionUi.set(this, true);
        
        mCurrentFilterSpecs.set(this, new RecipientFilterSpecification());
        mCurrentSortSpecs.set(this, new RecipientSortSpecification(true, false, false)); //Sort by name initially.

        mRecipientPagerData.set(this, {
            currentPage: 1,
            pageSize: 25,
            totalItems: 25,
            checkCurrentPageValidity: function(adjustmentToTotalItems) {
                let adjustedTotalItems = this.totalItems;
                if (Number.isInteger(adjustmentToTotalItems)) {
                    adjustedTotalItems += adjustmentToTotalItems;
                }

                let currentPageMax = this.currentPage * this.pageSize;

                return (currentPageMax <= adjustedTotalItems);
            }
        });

        mActiveRecipientSubscriptions.set(this, []);
        mSubscriptionTypesInfo.set(this, []);
        let subscriptionTypesData = mSubscriptionDataSvc.get(this).getSubscriptionTypesInfo();

        Array.prototype.push.apply(mSubscriptionTypesInfo.get(this), Object.keys(subscriptionTypesData).map(
            (sType) => {
                return {
                    subTypeId: sType,
                    subTypeDisplayName: subscriptionTypesData[sType],
                    currentSubIdToAdd: null,
                    canRemoveSubs: false,
                    subMessages: []
                };
            }
        ));

        //Initialize Subscription Editor Parameters
        //

        const editorDialogBackDropClass = "tbIn";
        const editorDialogWindowClass = "tbIn";
        const editorDialogSize = "lg";
        
        mSubsEditorParams.set(this, {
            Target: {
                animation: true,
                template: TargetSubscriptionsEditorDialogController.template,
                controller: TargetSubscriptionsEditorDialogController,
                controllerAs: TargetSubscriptionsEditorDialogController.controllerAs,
                bindToController: true,
                backdropClass: editorDialogBackDropClass,
                windowClass: editorDialogWindowClass,
                size: editorDialogSize,
                resolve: {
                    targetSubscriptions: () => this.activeRecipientSubscriptions.filter(
                        (tSub) => (tSub.sub.subscriptionType === "Target")
                    )
                }
            },
            OfficeType: {
                animation: true,
                template: OfficeTypeSubscriptionsEditorDialogController.template,
                controller: OfficeTypeSubscriptionsEditorDialogController,
                controllerAs: OfficeTypeSubscriptionsEditorDialogController.controllerAs,
                bindToController: true,
                backdropClass: editorDialogBackDropClass,
                windowClass: editorDialogWindowClass,
                size: editorDialogSize,
                resolve: {
                    officeTypeSubscriptions: () => this.activeRecipientSubscriptions.filter(
                        (tSub) => (tSub.sub.subscriptionType === "OfficeType")
                    )
                }
            },
            StateEvent: {
                animation: true,
                template: StateEventSubscriptionsEditorDialogController.template,
                controller: StateEventSubscriptionsEditorDialogController,
                controllerAs: StateEventSubscriptionsEditorDialogController.controllerAs,
                bindToController: true,
                backdropClass: editorDialogBackDropClass,
                windowClass: editorDialogWindowClass,
                size: editorDialogSize,
                resolve: {
                    stateEventSubscriptions: () => this.activeRecipientSubscriptions.filter(
                        (tSub) => (tSub.sub.subscriptionType === "StateEvent")
                    )
                }
            },
            StateOfficeElection: {
                animation: true,
                template: StateOfficeElectionSubscriptionsEditorDialogController.template,
                controller: StateOfficeElectionSubscriptionsEditorDialogController,
                controllerAs: StateOfficeElectionSubscriptionsEditorDialogController.controllerAs,
                bindToController: true,
                backdropClass: editorDialogBackDropClass,
                windowClass: editorDialogWindowClass,
                size: editorDialogSize,
                resolve: {
                    stateSubscriptions: () => this.activeRecipientSubscriptions.filter(
                        (tSub) => (tSub.sub.subscriptionType === "StateId")
                    ),
                    officeSubscriptions: () => this.activeRecipientSubscriptions.filter(
                        (tSub) => (tSub.sub.subscriptionType === "Office")
                    ),
                    electionSubscriptions: () => this.activeRecipientSubscriptions.filter(
                        (tSub) => (tSub.sub.subscriptionType === "Election")
                    )
                }
            }
        });

        mSubsEditorMessages.set(this, new Map(
            Array.from(subEditorParamsMap.keys()).map(
                (subTypeName) => [subTypeName, new Map()])));

        mScopeSvc.get(this).$on("$destroy", () => {
            let subMgrRootElement = angular.element("#tbSubscriptionManagementRoot");
            kendo.destroy(subMgrRootElement);
        });

        $document.attr("title", tbAdministrationStateConstants.managementPage.pageTitleBase);

        this.loadRecipientsData().then(
            () => {
                this._postInitialDataLoad();
            },
            (errorMessage) => {
                mErrorMessageQueue.get(this).publishMessage(errorMessage, undefined);
            }
        );

        $scope.$watch(
            () => this.filterSpecs.allFilterContent,
            (newVal, oldVal) => {
                if (newVal !== oldVal) {
                    this.applyFilterSort(true);
                }
            }
        );

        $scope.$watch(
            () => this.sortSpecs.sortEnabled,
            (newVal, oldVal) => {
                if (newVal !== oldVal) {
                    this.applyFilterSort();
                }
            }
        );

        $scope.$watch(
            () => this.sortSpecs.sortContent,
            (newVal, oldVal) => {
                if (newVal !== oldVal) {
                    this.applyFilterSort();
                }
            }
        );
    }

    $onInit() {
        const anchorScrollOffsetTarget = `#${TbAppConstants.mainNavBarElementId}`;
        mAnchorScrollSvc.get(this).yOffset = angular.element(anchorScrollOffsetTarget);
    }

    get errorMessages() {
        return mErrorMessageQueue.get(this).messages;
    }

    get statusMessages() {
        return mStatusMessageQueue.get(this).messages;
    }

    closeErrorMessage(msgIdx) {
        mErrorMessageQueue.get(this).removeMessageAtIdx(msgIdx);
    }

    closeStatusMessage(msgIdx) {
        mStatusMessageQueue.get(this).removeMessageAtIdx(msgIdx);
    }

    get operationInProgress() {
        return mOperationInProgress.get(this);
    }

    set operationInProgress(value) {
        mOperationInProgress.set(this, value);
    }

    get currentProgress() {
        return mCurrentProgress.get(this);
    }

    get currentProgressPct() {
        const currentProgressRealPct = Math.trunc((this.currentProgress / this.currentOperationMax) * 100);
        const currentProgressDisplayPct = currentProgressRealPct >= 100 ? 100 : currentProgressRealPct;
        return `${currentProgressDisplayPct}%`;
    }

    set currentProgress(value) {
        mCurrentProgress.set(this, value);
    }

    get currentOperationMax() {
        return mCurrentOperationMax.get(this);
    }

    set currentOperationMax(value) {
        if (this.currentProgress > value) {
            this.currentProgress = value;
        }
        mCurrentOperationMax.set(this, value);
    }

    get currentOperationStatus() {
        return mCurrentOperationStatus.get(this);
    }

    set currentOperationStatus(value) {
        // should be a valid bootstrap category,
        // for example, "success" or "danger"
        return mCurrentOperationStatus.set(this, value);
    }

    get rcptActionMenuIsOpen() {
        return mRecipientActionMenuIsOpen.get(this);
    }

    set rcptActionMenuIsOpen(updatedVal) {
        mRecipientActionMenuIsOpen.set(this, updatedVal);
    }

    get showRecipientTableFilters() {
        return mShowRecipientTableFilters.get(this);
    }

    get createRecipientMode() {
        return mInCreateRecipientMode.get(this);
    }

    get activeRecipient() {
        return mActiveRecipient.get(this);
    }

    set activeRecipient(nowActiveRecipient) {
        mActiveRecipient.set(this, nowActiveRecipient);
    }

    get activeRecipientIdx() {
        return mActiveRecipientIdx.get(this);
    }

    set activeRecipientIdx(updatedIdx) {
        if ((updatedIdx == null) || ((Number.isInteger(updatedIdx)) && (updatedIdx >= 0))) {
            mActiveRecipientIdx.set(this, updatedIdx);
        }
        else {
            throw new TypeError("Cannot set recipient index to value that is not a positive integer, 0, or null");
        }
    }

    get userInfo() {
        return mUserInfoSvc.get(this);
    }

    get sortSpecs() {
        return mCurrentSortSpecs.get(this);
    }

    get filterSpecs() {
        return mCurrentFilterSpecs.get(this);
    }

    get recipientPagerData() {
        return mRecipientPagerData.get(this);
    }

    get recipientData() {
        return mRecipientData.get(this);
    }

    get recipientDataAppendix() {
        return mRecipientDataAppendix.get(this);
    }

    get activeRecipientSubscriptions() {
        return mActiveRecipientSubscriptions.get(this);
    }

    get subscriptionTypesInfo() {
        return mSubscriptionTypesInfo.get(this);
    }

    get internalUserHelpMessage() {
        return mUserMessagesSvc.get(this).getUserMessage({msgId: 0x1020});
    }

    get showSelectBatchRecipientsUi() {
        return mShowRecipientBatchSelectionUi.get(this);
    }

    //Begin methods
    _postInitialDataLoad() {
        let recipientIdFromStateParams = mUIRouterStateParams.get(this).rcptId;
        if (recipientIdFromStateParams != null) {

            let rIdFromUrl = Number.parseInt(recipientIdFromStateParams);

            if ((Number.isInteger(rIdFromUrl)) &&
                (rIdFromUrl > 0))
            {
                let rcptIdx = this.recipientData.findIndex(
                    (rcptEntry) => (rcptEntry.recipientId === rIdFromUrl)
                );

                if (rcptIdx >= 0) {
                    mTimeoutSvc.get(this)(() => {
                        this.editRecipient(rcptIdx);
                    }, 20);
                }
                else {
                    mRecipientDataSvc.get(this).getRecipientCore(rIdFromUrl).then(
                        (rcptData) => {
                            this.recipientData.unshift(rcptData);
                            this.recipientDataAppendix.unshift({
                                rcptId: rcptData.recipientId,
                                isBeingEdited: false,
                                isSelected: this.isRecipientSelected(rcptData.recipientId)
                            });

                            mTimeoutSvc.get(this)(() => {
                                this.editRecipient(0);
                            }, 20);

                        },
                        (errorMessage) => {
                            mErrorMessageQueue.get(this).publishMessage(errorMessage, undefined);
                        }
                    );
                }
            }
        }
    }

    toggleRecipientTableFilters() {
        let showRecipientTableFilters = mShowRecipientTableFilters.get(this);
        mShowRecipientTableFilters.set(this, !showRecipientTableFilters);
    }

    toggleRecipientBatchSelectionUiVisibility() {
        let batchSelectionUiIsVisible = mShowRecipientBatchSelectionUi.get(this);
        mShowRecipientBatchSelectionUi.set(this, !batchSelectionUiIsVisible);
    }

    loadRecipientsData() {
        let pageRequested = this.recipientPagerData.currentPage;
        let currentPageSize = this.recipientPagerData.pageSize;

        let selectedFilterSpecs = RecipientFilterSpecification.FilterKeys.filter(
            (filterKey) => (this.filterSpecs[filterKey].filterContent != null)
        ).map(
            (filterKey) => this.filterSpecs[filterKey]
        );

        let selectedSortSpecs = RecipientSortSpecification.SortKeys.filter(
            (sortKey) => this.sortSpecs[sortKey].isEnabled
        ).map(
            (sortKey) => this.sortSpecs[sortKey].spec
        );

        let selectedSortSpec = (selectedSortSpecs.length > 0) ? selectedSortSpecs[0] : null;

        return mRecipientDataSvc.get(this)
            .getAllRecipients(pageRequested, currentPageSize, selectedFilterSpecs, selectedSortSpec)
            .then(
                (recipients) => {
                    this.recipientData.length = 0;

                    this.recipientPagerData.totalItems = recipients.total;
                    this.recipientPagerData.pageSize = recipients.pageSize;
                    this.recipientPagerData.currentPage = recipients.currentPage;


                    Array.prototype.push.apply(this.recipientData, recipients.recipientData);
                    let numReturnedRecipients = this.recipientData.length;

                    this.recipientDataAppendix.length = 0;
                    for (let rcptIdx = 0; rcptIdx < numReturnedRecipients; ++rcptIdx) {
                        const currentRecipientId = this.recipientData[rcptIdx].recipientId;
                        this.recipientDataAppendix.push({
                            rcptId: currentRecipientId,
                            isBeingEdited: false,
                            isSelected: this.isRecipientSelected(currentRecipientId)
                        });
                    }
                    mLogSvc.get(this).debug("rcpt data load: rcpt data appendix", this.recipientDataAppendix);
                    //this.recipientDataLoaded = true;
                })
            .catch((errorResponse) => {
                mErrorMessageQueue.get(this).publishMessage(errorResponse, undefined);
            });
    }

    updateProgress(value) {
        mTimeoutSvc.get(this)(() => {
            this.currentProgress = value > this.currentOperationMax ? this.currentOperationMax : value;
        },0);
    }

    incrementProgress(value) {
        mTimeoutSvc.get(this)(() => {
            let updatedProgress = this.currentProgress + value;
            if (updatedProgress > this.currentOperationMax) {
                updatedProgress = this.currentOperationMax;
            }
            this.currentProgress = updatedProgress;
        },0);
    }

    initOperation(opMax, opStart = 0) {
        const scopeSvc = mScopeSvc.get(this);
        const anchorScrollSvc = mAnchorScrollSvc.get(this);
        mTimeoutSvc.get(this)(() => {
            this.operationInProgress = false;
            this.currentOperationMax = opMax;
            this.markOkOperationStatus();
            this.currentProgress = opStart > opMax ? opMax : opStart;
            this.operationInProgress = true;
            scopeSvc.$apply();
        },0)
        .then(() => {
            anchorScrollSvc("tbSubscriptionManagementRoot");
        });
    }

    endOperation(waitTime = 0) {
        const scopeSvc = mScopeSvc.get(this);
        mTimeoutSvc.get(this)(() => {
            this.operationInProgress = false;
            scopeSvc.$apply();
        }, waitTime);
    }

    markErrorOperationStatus() {
        mTimeoutSvc.get(this)(() => {
           this.currentOperationStatus = errorMessageQueueType;
        });
    }

    markOkOperationStatus() {
       mTimeoutSvc.get(this)(() => {
           this.currentOperationStatus = statusMessageQueueType;
       });
    }

    recipientPageChanged() {
        this.loadRecipientsData().then(
            () => mAnchorScrollSvc.get(this)("recipientsListTop")
        );
    }

    startCreateRecipient(e) {
        if (e != null) {
            e.preventDefault();
        }

        if (mInCreateRecipientMode.get(this)) {
            return;
        }

        if (this.activeRecipientIdx != null) {
            mErrorMessageQueue.get(this).publishMessage("Please finish editing the current recipient before adding a new one.", undefined);
            return;
        }

        let blankRecipient = Recipient.CreateBlankRecipient();
        mInCreateRecipientMode.set(this, true);

        let currentScope = mScopeSvc.get(this);

        this.recipientData.unshift(blankRecipient);
        if ((currentScope.tbNewRecipientForm != null) &&
            (angular.isDefined(currentScope.tbNewRecipientForm.$setPristine)))
        {
            currentScope.tbNewRecipientForm.$setPristine();
        }
    }

    cancelCreateRecipient() {
        if (!(mInCreateRecipientMode.get(this))) {
            return;
        }

        if (this.recipientData[0].recipientId === 0) {
            this.recipientData.shift();
        }

        mInCreateRecipientMode.set(this, false);
    }

    saveNewRecipient() {
        if (!(mInCreateRecipientMode.get(this))) {
            return;
        }

        let newRecipientData = this.recipientData[0];

        if (newRecipientData.recipientId !== 0) {
            mErrorMessageQueue.get(this).publishMessage(
                "An unknown error occurred before we could send your request. " +
                "Please file a bug report and refresh the page.",
                undefined
            );
            this.recipientData.shift();
            
            mInCreateRecipientMode.set(this, false);
        }

        let createMessageId = mStatusMessageQueue.get(this).publishMessage(
            "Saving ...",
            undefined
        );

        mRecipientDataSvc.get(this)
            .createRecipient(newRecipientData)
            .then(
                (createdRecipient) => {
                    this.recipientData[0] = createdRecipient;
                    this.recipientDataAppendix.unshift({
                        rcptId: createdRecipient.recipientId,
                        isBeingEdited: false,
                        isSelected: this.isRecipientSelected(createdRecipient.recipientId)
                    });

                    mLogSvc.get(this).debug("save new: rcpt data appendix", this.recipientDataAppendix);
                    mStatusMessageQueue.get(this).removeMessageById(createMessageId);
                    mInCreateRecipientMode.set(this, false);
    
                    this.editRecipient(0, null);
    
                }, 
                (errorMessage) => {
                    mStatusMessageQueue.get(this).removeMessageById(createMessageId);
                    mErrorMessageQueue.get(this).publishMessage("Unable to create recipient: " + errorMessage, undefined);
                }
            );
    }

    editRecipient(rcptIdx, e) {

        if (e != null) {
            e.preventDefault();
        }

        let loadingMessageId = mStatusMessageQueue.get(this).publishMessage("Loading...", undefined);

        if (this.activeRecipientIdx != null) {
            this.recipientDataAppendix[this.activeRecipientIdx].isBeingEdited = false;
        }

        let tgtRcptId = this.recipientData[rcptIdx].recipientId;
        //Get a full, fresh copy of the recipient from the server for editing.
        return mRecipientDataSvc.get(this)
        .getRecipient(tgtRcptId)
        .then(
            (freshRcpt) => {
                // let's make sure we're still dealing with the same editing target that we were when we asked the server for the data.
                let tgtCheckId = this.recipientData[rcptIdx].recipientId;
                if (tgtCheckId === freshRcpt.recipientId) {
                    this.activeRecipientIdx = rcptIdx;
                    this.recipientData[this.activeRecipientIdx] =
                        Recipient.CopyRecipient(freshRcpt);
                    mActiveRecipient.set(this, freshRcpt);
                    this.recipientDataAppendix[this.activeRecipientIdx].isBeingEdited = true;

                    if ((mScopeSvc.get(this).tbEditRecipientForm != null))
                    {
                        mScopeSvc.get(this).tbEditRecipientForm.$setPristine();
                    }

                    mDocumentSvc.get(this).attr(
                        "title",
                        `${tbAdministrationStateConstants.managementPage.pageTitleBase}: ${this.activeRecipient.recipientName}`
                    );

                    this.getSubscriptionTargetNames(this.activeRecipient)
                    .then(
                        () => {
                            mTimeoutSvc.get(this)(() => {
                                this.subscriptionTypesInfo.forEach((subTypeInfo) => {
                                    this.refreshCanRemoveSubs(subTypeInfo);
                                });
                            }, 0);
                            mStatusMessageQueue.get(this).removeMessageById(loadingMessageId);
                            mStatusMessageQueue.get(this).publishMessage("Complete", 2000);

                            mUIRouterStateSvc.get(this).go(
                                `${tbAdministrationStateConstants.rootStateName}.${tbAdministrationStateConstants.managementPage.stateName}`,
                                { rcptId: tgtRcptId }
                            );
                        },
                        (errMessage) => mQSvc.get(this).reject(errMessage)
                    );
                }
            },
            (errorResponse) => {
                mStatusMessageQueue.get(this).removeMessageById(loadingMessageId);
                mErrorMessageQueue.get(this).publishMessage(errorResponse, undefined);
            }
        );
    }

    deleteRecipient(rcptIdx, e) {

        if (e != null) {
            e.preventDefault();
        }

        let logSvc = mLogSvc.get(this);

        if (!((Number.isInteger(rcptIdx)) &&
            (rcptIdx >= 0) &&
            (rcptIdx < this.recipientData.length)))
        {
            logSvc.get(this).error("Delete recipient: invalid index value into recipient data");
            mErrorMessageQueue.get(this).publishMessage("Cannot delete recipient: invalid recipient.");
            return;
        }

        let recipientToDelete = this.recipientData[rcptIdx];

        let deleteRecipientDialogParams = {
            animation: true,
            template: ConfirmDeleteDialogController.template,
            controller: ConfirmDeleteDialogController,
            controllerAs: ConfirmDeleteDialogController.controllerAs,
            bindToController: true,
            backdropClass: "tbIn",
            windowClass: "tbIn",
            size: "lg",
            resolve: {
                deleteTarget: () => recipientToDelete,
                deleteTargetDisplayNameGetter: () => function (rcpt) { return rcpt.recipientName; }
            }
        };

        let delConfirmDialogInstance = mUIBModalSvc.get(this)
                                        .open(deleteRecipientDialogParams);

        const userCancelledModalByClickingBackdropResultStatusMessage = "backdrop click";

        return Promise.resolve(delConfirmDialogInstance.result)
        .catch((dismissReason) => {
            if ((dismissReason === ConfirmDeleteDialogController.UserCancelledStatusMessage) ||
                (dismissReason === userCancelledModalByClickingBackdropResultStatusMessage)) {

                throw new SubscriptionWorkflowCancelledError({userCancelled: true});
            }
            else {
                throw new SubscriptionWorkflowError({
                    errorMessage: "An error occurred showing the confirmation dialog",
                    errorData: dismissReason
                });
            }
        })
        .then((dialogResult) => {
            Assert.StrictAssert(
                dialogResult instanceof ConfirmDeleteDialogResult,
                "Unexpected Result Type from Confirm Delete Dialog."
            );

            if (dialogResult.status === ConfirmDeleteDialogController.UserConfirmedStatusMessage) {
                logSvc.debug(`Delete confirmed. Deleting recipient with Id #${recipientToDelete.recipientId}`);

                return mRecipientDataSvc.get(this)
                                        .deleteRecipient(recipientToDelete.recipientId);
            }
            else {
                throw new SubscriptionWorkflowError({
                    errorMessage: "Unexpected Delete Confirm Dialog Result",
                    errorData: dialogResult
                });
            }
        })
        .then((successResult) => {
            if (successResult === true) {
                logSvc.debug("Recipient delete api call succeeded.");
                this.recipientData.splice(rcptIdx, 1);
                recipientToDelete = null;

                mStatusMessageQueue.get(this).publishMessage("Recipient deleted successfully.", 2000);

                //Handle this corner case:
                // Assume:
                //  - There are 101 total recipients.
                //  - The current page size is 20.
                //  - The user navigates to page 6.
                //
                // If the user then deletes recipient 101, we need to
                // detect that page 6 is no longer a valid page, and
                // request page 5 instead when we reload the recipient
                // data page.
                if (!this.recipientPagerData.checkCurrentPageValidity(-1)) {
                    logSvc.debug("Handling deletion of last item on current page corner case.");
                    this.recipientPagerData.currentPage -= 1;
                }

                return this.loadRecipientsData();
            }
            else {
                throw new SubscriptionWorkflowError({
                    errorMessage: "The delete succeeded, but an unexpected error during post-delete processing. See the error log for further details.",
                    errorData: successResult
                });
            }
        })
        .catch((errorData) => {
            if ((errorData instanceof SubscriptionWorkflowError) || (errorData instanceof SubscriptionWorkflowCancelledError)) {
                throw errorData;
            }
            else {
                throw new SubscriptionWorkflowError({
                    errorMessage: "An error occurred while deleting the recipient. Please check the log for details.",
                    errorData: errorData
                });
            }
        })
        .catch((errorResult) => {

            if (errorResult instanceof SubscriptionWorkflowCancelledError) {
                const messageInfo = errorResult.wasUserCancelled ? "by user" : "by workflow";
                logSvc.info(`Recipient delete cancelled ${messageInfo}`);
                return;
            }

            if (errorResult instanceof SubscriptionWorkflowError) {
                logSvc.error(`Subscription Workflow Error: ${errorResult.message}`);
                logSvc.error(errorResult.errorData);
                mErrorMessageQueue.get(this).publishMessage(
                    "Attempt to delete recipient failed. See the error log for further details.",
                    undefined
                );
                return; //Swallow the error.
            }


            // Throw unexpected errors so they halt the page.
            mErrorMessageQueue.get(this).publishMessage(
                "An unexpected error occurred while deleting the recipient. Please refresh the page, or contact support if this behavior happens repeatedly.",
                undefined
            );
            throw errorResult;
        });
    }

    saveRcptInfoChanges(e) {
        if (e != null) {
            e.preventDefault();
        }

        mRecipientDataSvc.get(this)
            .updateRecipient(this.activeRecipient)
            .then(
                (updatedRecipient) => {
                    this.recipientData[this.activeRecipientIdx] = updatedRecipient;
                    this.activeRecipient = Recipient.CopyRecipient(updatedRecipient);

                    mStatusMessageQueue.get(this).publishMessage(
                        "Saved Successfully.",
                        2000
                    );

                },
                (errorResponse) => {
                    mErrorMessageQueue.get(this).publishMessage(errorResponse, undefined);
                }
            );
    }

    cancelRcptEdit(e) {
        if (e != null) {
            e.preventDefault();
        }

        if (this.activeRecipientIdx != null) {
            this.recipientDataAppendix[this.activeRecipientIdx].isBeingEdited = false;
        }

        this.activeRecipientIdx = null;
        this.activeRecipient = null;

        mDocumentSvc.get(this).attr("title",
            tbAdministrationStateConstants.managementPage.pageTitleBase
        );

        mUIRouterStateSvc.get(this).go(
            `${tbAdministrationStateConstants.rootStateName}.${tbAdministrationStateConstants.managementPage.stateName}`,
            { rcptId: null }
        );
    }

    popSubEditorMessages(subType, subId) {
        const subEditorMessagesForSubType = mSubsEditorMessages.get(this).get(subType) || new Map();
        // noinspection UnnecessaryLocalVariableJS
        const subEditorMsgs = subEditorMessagesForSubType.get(subId) || [];
        if (subEditorMsgs.length > 0) {
            subEditorMessagesForSubType.delete(subId);
        }
        return subEditorMsgs;
    }

    getSubscriptionTargetNames(rcpt) {
        let logSvc = mLogSvc.get(this);

        this.activeRecipientSubscriptions.length = 0;

        let getSubscriptionTargetNamesPromises = [];

        let subscriptionTargetBuckets = {};

        rcpt.recipientSubscriptions.forEach((rSub) => {
            if (angular.isUndefined(subscriptionTargetBuckets[rSub.subscriptionType])) {
                subscriptionTargetBuckets[rSub.subscriptionType] = {
                    subscriptionType: rSub.subscriptionType,
                    subscriptions: []
                };
            }
            subscriptionTargetBuckets[rSub.subscriptionType].subscriptions.push(rSub);
        });

        Object.keys(subscriptionTargetBuckets).forEach(
            (subBucketName) => {

                let subBucket = subscriptionTargetBuckets[subBucketName];

                let getSubscriptionTargetNameBucketPromise =
                    mSubscriptionDataSvc.get(this)
                        .getSubscriptionTargetBatch(subBucket)
                        .then(
                            (subTargets) => {

                                logSvc.debug(`Subscriptions in ${subBucket.subscriptionType} Retrieval Bucket:`);
                                logSvc.debug(subBucket.subscriptions);

                                logSvc.debug("Retrieved Subscription Targets:");
                                logSvc.debug(subTargets);


                                for (let subTargetIdx = subTargets.length - 1; subTargetIdx >= 0; subTargetIdx--) {
                                    let subTarget = subTargets[subTargetIdx];
                                    let subTargetId = subTarget.subscribableId;

                                    if (subTargetId == null) {
                                        throw new Error("Subscribable Obj is missing Id property");
                                    }

                                    let rSub = subBucket.subscriptions.find(function (subInBucket) {
                                        let idOfSubInBucket = subInBucket.subscriptionTargetId;
                                        return (idOfSubInBucket === subTargetId);
                                    });

                                    if (angular.isUndefined(rSub)) {
                                        throw new Error("Unable to find matching subscription for subscribable");
                                    }

                                    const msgsForSub = this.popSubEditorMessages(
                                        subBucket.subscriptionType,
                                        subTargetId
                                    );

                                    this.activeRecipientSubscriptions.push(new ActiveRecipientSubscriptionViewModel({
                                        sub: rSub,
                                        tgt: subTarget,
                                        shouldRemove: false,
                                        subMessages: msgsForSub
                                    }));
                                }

                                return subTargets.length;
                            },
                            (errMessage) => mQSvc.get(this).reject(errMessage)

                        );

                getSubscriptionTargetNamesPromises.push(getSubscriptionTargetNameBucketPromise);
            }
        );

        const getSubscriptionTargetNamesTaskPromise =
            mQSvc.get(this).all(getSubscriptionTargetNamesPromises)
                .then(
                    (subCounts) => {
                        let subCountTotal = 0;
                        subCounts.forEach(function(subCount) {
                            subCountTotal += subCount;
                        });

                        return subCountTotal;

                    },
                    (errMessages) => {
                        let combinedErrMessage = errMessages.join("\n");
                        mQSvc.get(this).reject(combinedErrMessage);
                    }
                );

        return getSubscriptionTargetNamesTaskPromise;
    }

    getSubscriptionTypePredicate(chosenSubType) {
        return function(rSub) {
            return (rSub.sub.subscriptionType === chosenSubType);
        };
    }

    refreshCanRemoveSubs(subTypeInfoObj) {
        mLogSvc.get(this).debug("Refreshing Can Remove Subs");

        let subsToRemove = this.activeRecipientSubscriptions.filter(
            (rSub) => ((rSub.sub.subscriptionType === subTypeInfoObj.subTypeId) && (rSub.shouldRemove))
        );

        subTypeInfoObj.canRemoveSubs = (subsToRemove.length > 0);
    }

    _addSubscriptionForRecipient({recipient, subTgtType, idOfSubTgt}) {
        let subToAdd = new Subscription(
            0,
            idOfSubTgt,
            subTgtType,
            Subscription.realTimeSubscriptionTypeName,
            null
        );
    
        return mSubscriptionDataSvc.get(this)
            .addSubscription(recipient, subToAdd);
    
    }

    // This only acts on the active recipient.
    // It's not used anywhere and can likely be removed
    addSubscription(subTgtType, idOfSubTgt) {
        return this._addSubscriptionForRecipient({
            recipient: this.activeRecipient,
            subTgtType: subTgtType,
            idOfSubTgt: idOfSubTgt
        });
    }

    // This only acts on the active recipient.
    // It's not used anywhere and can likely be removed
    removeSubscription(subTypeId, subToDel, e) {
        if (e != null) {
            e.preventDefault();
        }

        if ((subTypeId != null) &&
            (subToDel != null))
        {
            mSubscriptionDataSvc.get(this)
                .removeSubscription(this.activeRecipient, subToDel)
                .then(
                    () => {
                        this.editRecipient(this.activeRecipientIdx, null);
                    },
                    (errorReason) => {
                        mErrorMessageQueue.get(this).publishMessage(errorReason, undefined);
                    }
                );
        }
        else {
            mLogSvc.get(this).error("Unable to remove subscription: either subscription type id or subscription to delete (or both) was null or missing");
        }
    }

    removeSelectedSubscriptions(subTypeInfo, e) {
        if (e != null) {
            e.preventDefault();
        }

        if (subTypeInfo == null) {
            mLogSvc.get(this).info("Ignored: Remove Selected Subscriptions Called With Null/Undefined Subscription Type Info Object.");
        }

        mTimeoutSvc.get(this)(() => {
            subTypeInfo.canRemoveSubs = false;
        },0);

        const subTypeId = subTypeInfo.subTypeId;

        // DELETE SUB FLOW STARTS HERE
        let subsToRemove = this.activeRecipientSubscriptions.filter(
            (rSub) => ((rSub.sub.subscriptionType === subTypeId) && (rSub.shouldRemove))
        )
        .map((rSubObj) => rSubObj.sub);

        const selectedRecipientsCount = this._getSubUpdateBatchRecipientIds(this.activeRecipientIdx).length;
        const subsToRemoveCount = subsToRemove.length;

        let removeSelectedPromises = [];

        // Notes: subToDel is technically targeted at the sub for the active recipient, but sub delete doesn't
        // actually care if the sub obj is actually for the recipient for which delete is being called
        // So we can simply call delete on a sub obj that isn't owned by the active recipient
        // when we handle batch removals. (Api calls only require sub target type, target id and recipient id)
        this.initOperation((subsToRemoveCount + 1) * selectedRecipientsCount);
        subsToRemove.forEach(
            (subToDel) => {
                const removeSelectedPromise =
                    this._removeSubForSelectedRecipients(subToDel)
                        .then((val) => {
                            this.incrementProgress(1);
                            return val;
                        });

                removeSelectedPromises.push(removeSelectedPromise);
            }
        );

        // //Testing, mark result status as error in 10s.
        // mTimeoutSvc.get(this)(() => {
        //     this.markErrorOperationStatus();
        // }, 10000);

        let removeAllSelectedPromise = mQSvc.get(this).all(removeSelectedPromises);

        removeAllSelectedPromise
        .then(() => {
            this.editRecipient(this.activeRecipientIdx, null);
        })
        .catch((errorMessages) => {
            this.markErrorOperationStatus();
            let errMsgQueue = mErrorMessageQueue.get(this);
            errorMessages.forEach((errMsg) => {
                errMsgQueue.publishMessage(errMsg, undefined);
            });
            return mQSvc.get(this).reject(false);
        })
        .finally(() => {
            this.endOperation();
        });
    }

    _removeSubForSelectedRecipients(subToDel) {
        const qSvc = mQSvc.get(this);
        const subDataSvc = mSubscriptionDataSvc.get(this);

        const progressUpdateFn = () => {
            this.incrementProgress(1);
        };

        return this._getSubUpdateBatchRecipients(progressUpdateFn)
            .then((selectedRecipients) => qSvc.all(
                selectedRecipients.map(
                    (selectedRecipient) => subDataSvc.removeSubscription(selectedRecipient, subToDel).then(
                        (retVal) => {
                            progressUpdateFn();
                            return retVal;
                        }
                    )
                    .catch((errorMsg) => {
                        this.markErrorOperationStatus();
                        qSvc.reject(errorMsg);
                    })
                )));
    }

    openSubsEditor(subType, e) {
        const logSvc = mLogSvc.get(this);
        const qSvc = mQSvc.get(this);
        const userMsgSvc = mUserMessagesSvc.get(this);
        const statusMsgQ = mStatusMessageQueue.get(this);
        const errorMsgQ = mErrorMessageQueue.get(this);
        const subEditorMsgs = mSubsEditorMessages.get(this);

        if (e != null) {
            e.preventDefault();
        }

        let editorParamSetName = subEditorParamsMap.get(subType);

        if (angular.isUndefined(editorParamSetName)) {
            return;
        }

        let editorParams = mSubsEditorParams.get(this)[editorParamSetName];
        let modalInstance = mUIBModalSvc.get(this).open(editorParams);

        const calculateSubCount = (subObj) => {
            const subTypes = Object.keys(subObj);
            const subCount = subTypes.map((subType) => {
                const subIdsToAdd = subObj[subType];
                return subIdsToAdd != null ? subIdsToAdd.length : 0;
            }).reduce((currSum, subCountForType) => currSum + subCountForType, 0);
            return subCount;
        };

        const progressIncrementFn = () => {
            this.incrementProgress(1);
        };

        const markErrorStatusFn = () => {
            this.markErrorOperationStatus();
        };

        // Testing, mark result status as error in 10s.
        // mTimeoutSvc.get(this)(() => {
        //     markErrorStatusFn();
        // }, 10000);

        modalInstance.result.then(
            ({subs: subIdsToAddData, msgs: messages}) => {
                let addSubPromises = [];

                let subTypes = Object.keys(subIdsToAddData);
                const subAddCount = calculateSubCount(subIdsToAddData);
                const selectedRecipientCount = this._getSubUpdateBatchRecipientIds().length;
                //NOTE: This is the batch-augmented add subs workflow main driver
                this.initOperation(selectedRecipientCount * (subAddCount + 1));
                subTypes.forEach(
                    (subTypeToAdd) => {
                        let subIdsToAddList = subIdsToAddData[subTypeToAdd];

                        if (angular.isDefined(subIdsToAddList)) {
                            const subBatchUpdatePromises =
                                this._getSubUpdateBatchRecipients(progressIncrementFn).then(
                                    (recipientsToUpdate) => qSvc.all(recipientsToUpdate.map((rcptToUpdate) => qSvc.all(
                                        subIdsToAddList.map(
                                            (subIdToAdd) => this._addSubscriptionForRecipient({
                                                recipient: rcptToUpdate,
                                                subTgtType: subTypeToAdd,
                                                idOfSubTgt: subIdToAdd
                                            })
                                            .then((retVal) => {
                                                progressIncrementFn();
                                                return retVal;
                                            })
                                            .catch((errorMsg) => {
                                                markErrorStatusFn();
                                                const errorInfo = errorMsg.toString();
                                                return qSvc.reject(errorInfo);
                                            })
                                        )))));
                            addSubPromises.push(subBatchUpdatePromises);
                        }
                    }
                );

                messages.forEach(({
                                      subTargetType: subTargetTypeName,
                                      subTargetId: subId,
                                      msgs: msgIds
                                  }) => {
                    // yes, I too wish that ?. worked with this very old build stack
                    const subEditorMsgForSubType = subEditorMsgs.get(subTargetTypeName);
                    if (subEditorMsgForSubType != null) {
                        const subEditorMsgParts = msgIds.map(
                            (msgId) => userMsgSvc.getUserMessage({msgId: msgId})
                        );
                        const subEditorMsg = subEditorMsgParts.join(": ");
                        subEditorMsgForSubType.set(subId, [subEditorMsg]);
                    }
                    else {
                        logSvc.warn(userMsgSvc.getUserMessage({msgId: 0x3002}), subTargetTypeName, subId, msgIds);
                    }
                });

                let addAllPromise = qSvc.all(addSubPromises);

                return addAllPromise.then(
                    () => {
                        this.editRecipient(this.activeRecipientIdx, null);
                    },
                    (errorReason) => {
                        errorMsgQ.publishMessage(errorReason, undefined);
                    }
                ).finally(() => {
                    this.endOperation();
                });

            },
            (subEditorError) => {
                if (subEditorError instanceof SubscriptionEditorError) {
                    if (subEditorError.isError) {
                        logSvc.error("Subscription Editor Error", subEditorError.errorMessage);
                        logSvc.debug("Subscription Editor Error Details", subEditorError.errorData);
                        errorMsgQ.publishMessage(subEditorError.errorMessage, undefined);
                    }
                    else if (subEditorError.errorMessage === SubscriptionEditorError.EditorCancelledMsg) {
                        statusMsgQ.publishMessage(subEditorError.errorMessage, 2000);
                    }
                    else {
                        statusMsgQ.publishMessage(subEditorError.errorMessage, 4000);
                    }
                }
                else if (typeof subEditorError === "string") {
                    if (subEditorError === SubscriptionEditorError.EditorDialogBackdropClickedMsg) {
                        statusMsgQ.publishMessage(SubscriptionEditorError.EditorCancelledMsg, 2000);
                    }
                    else {
                        logSvc.error("Error returned from add subscription dialog", subEditorError);
                        errorMsgQ.publishMessage(`Add Subscription Error: ${subEditorError}`, undefined);
                    }
                }
                else {
                    logSvc.error("An unknown error occurred", subEditorError);
                    errorMsgQ.publishMessage("An unknown error occurred adding subscriptions.", undefined);
                }
            }
        );
    }

    applyFilterSort(goToTop) {
        if (goToTop == true) { //deliberately not strict equality
            this.recipientPagerData.currentPage = 1;
        }
        this.loadRecipientsData();
    }

    get selectedRecipientIds() {
        return mSelectedRecipientIds.get(this);
    }

    isRecipientSelected(rcptId) {
        return this.selectedRecipientIds.has(rcptId);
    }

    _syncSelectedRecipients({syncScope}) {
        const logSvc = mLogSvc.get(this);
        logSvc.debug("In Sync Selected Recipients");
        logSvc.debug("Selected Recipients:", this.selectedRecipientIds);
        logSvc.debug("data appendix pre-sync", this.recipientDataAppendix);
        this.recipientDataAppendix.forEach((appData) => {
            logSvc.debug("updating data appendix entry", appData);
            appData.isSelected = this.isRecipientSelected(appData.rcptId);
        });
        logSvc.debug("data appendix post-sync", this.recipientDataAppendix);

        if (syncScope) {
            const scopeSvc = mScopeSvc.get(this);
            mTimeoutSvc.get(this)(() => {
                scopeSvc.$apply();
            },0);
        }
    }

    updateSelectedRecipients(rcptIdx, e) {
        if (e != null) {
            e.preventDefault();
        }
        const tgtRcptId = this.recipientData[rcptIdx].recipientId;

        if (this.selectedRecipientIds.has(tgtRcptId)) {
            this.selectedRecipientIds.delete(tgtRcptId);
            mLogSvc.get(this).debug(`Removed recipient id ${tgtRcptId} from selected recipients`);
        }
        else {
            this.selectedRecipientIds.add(tgtRcptId);
            mLogSvc.get(this).debug(`Added recipient id ${tgtRcptId} to selected recipients`);
        }

        this._syncSelectedRecipients({syncScope: true});
    }

    selectAllVisibleRecipients() {
        this.recipientData.map((r) => r.recipientId).forEach((rId) => {
            this.selectedRecipientIds.add(rId);
        });
        this._syncSelectedRecipients({syncScope: true});
    }

    deselectAllVisibleRecipients() {
        this.recipientData.map((r) => r.recipientId).forEach((rId) => {
            this.selectedRecipientIds.delete(rId);
        });
        this._syncSelectedRecipients({syncScope: true});

    }

    clearSelectedRecipients() {
        this.selectedRecipientIds.clear();
        this._syncSelectedRecipients({syncScope: true});
    }

    getEditHeader(editTgtIdx) {
        let selectedIdxCount = this.selectedRecipientIds.size;
        if (this.selectedRecipientIds.has(editTgtIdx)) {
            selectedIdxCount--;
        }
        switch(selectedIdxCount) {
            case 0:
                return "";
            case 1:
                return " & 1 other";
            default:
                return ` & ${selectedIdxCount} others`;
        }
    }

    _getSubUpdateBatchRecipientIds(currentRecipientId) {
        const batchRecipientIds = Array.from(this.selectedRecipientIds);
        if ((currentRecipientId != null) && (!this.selectedRecipientIds.has(currentRecipientId))) {
            batchRecipientIds.push(currentRecipientId);
        }
        return batchRecipientIds;
    }

    _getSubUpdateBatchRecipients(progressUpdateCallback) {
        const recipientDataSvc = mRecipientDataSvc.get(this);
        const qSvc = mQSvc.get(this);
        const logSvc = mLogSvc.get(this);
        let activeRecipientId = this.activeRecipient != null ? this.activeRecipient.recipientId : null;
        const recipientIdsToUpdate = this._getSubUpdateBatchRecipientIds(activeRecipientId);

        const loadRecipientsToUpdatePromise = qSvc.all(
            recipientIdsToUpdate.map((rId) => recipientDataSvc.getRecipientCore(rId).then((rcptCore) => {
                if (progressUpdateCallback != null) {
                    progressUpdateCallback();
                }
                return rcptCore;
            })));

        return loadRecipientsToUpdatePromise.catch((errReasons) => {
            try {
                const errReasonsData = JSON.stringify(errReasons);
                const combinedErrMessage = "Error retrieving selected recipients before batch update:\n" + errReasonsData;
                return qSvc.reject(combinedErrMessage);
            }
            catch(errUnpackErr) {
                logSvc.error("An unknown error occurred during recipient load", errUnpackErr);
                return qSvc.reject("An unknown error occurred during recipient load");
            }
            finally {
                this.markErrorOperationStatus();
            }
        });
    }
}

TbSubscriptionManagementController.$inject = [
    "$scope",
    "$document",
    "$timeout",
    "$q",
    "$anchorScroll",
    "$log",
    "$state",
    "$transition$",
    "$uibModal",
    "RecipientData",
    "SubscriptionData",
    "UserInfoService",
    "UserMessages"
];

TbSubscriptionManagementController.dependencies = DependencyListBuilder([
    ConfirmDeleteDialogController.dependencies,
    TargetSubscriptionsEditorDialogController.dependencies,
    OfficeTypeSubscriptionsEditorDialogController.dependencies,
    StateEventSubscriptionsEditorDialogController.dependencies,
    StateOfficeElectionSubscriptionsEditorDialogController.dependencies,
    [
        ngSanitize,
        ngAnimate,
        ngAria,
        uiRouter,
        uiBootstrap,
        kendoDirectivesModuleName,
        SortFilterColumnHeaderComponentModule,
        RecipientDataServiceModule,
        SubscriptionDataServiceModule,
        UserInfoServiceModule,
        UserMessagesServiceModule
    ]
]);

TbSubscriptionManagementController.template = require("./TbSubscriptionManagement.tpl.html");
TbSubscriptionManagementController.controllerAs = "tbSubMgr";

export default TbSubscriptionManagementController;



//////////////////
// WEBPACK FOOTER
// ./src/views/trackerbase-admin/TbSubscriptionManagementController.js
// module id = Djr7
// module chunks = 1